import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  Search,
  X,
  Plus,
  Minus,
  Pencil,
  Check,
  ChevronLeft,
  ShoppingCart,
  AlertCircle,
  Building2,
  User,
} from 'lucide-react';
import { adminCustomers, adminProducts, adminOrders } from '../../services/api';
import { formatProductDisplay } from '../../utils/formatProduct';

// ── Helpers ────────────────────────────────────────────────────────────────────
const fmt = (v) =>
  v != null
    ? Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
    : '—';

const PAYMENT_LABEL = { invoice: 'Invoice', credit_card: 'Credit Card', both: 'Invoice / Credit Card' };

// ── Main component ─────────────────────────────────────────────────────────────
export default function CreateOrder() {
  const navigate        = useNavigate();
  const [searchParams]  = useSearchParams();
  const preselectedId   = searchParams.get('customer');

  // ── Step ──────────────────────────────────────────────────────────────────────
  const [step, setStep] = useState(preselectedId ? 2 : 1);

  // ── Step 1 — customer search ──────────────────────────────────────────────────
  const [customerQuery,   setCustomerQuery]   = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const debounceRef = useRef(null);

  const searchCustomers = useCallback(async (q) => {
    if (!q || q.length < 2) { setCustomerResults([]); return; }
    setCustomerLoading(true);
    try {
      const res = await adminCustomers.list({ search: q });
      const rows = res?.data?.data || [];
      setCustomerResults(rows.slice(0, 10));
    } catch {
      setCustomerResults([]);
    } finally {
      setCustomerLoading(false);
    }
  }, []);

  const handleCustomerQueryChange = (e) => {
    const q = e.target.value;
    setCustomerQuery(q);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchCustomers(q), 300);
  };

  // Preload customer if ?customer= is set
  useEffect(() => {
    if (!preselectedId) return;
    adminCustomers.get(preselectedId)
      .then((res) => {
        const c = res?.data?.data;
        if (c) selectCustomer(c);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedId]);

  // ── Step 2 — product search + order building ──────────────────────────────────
  const [productQuery,   setProductQuery]   = useState('');
  const [productResults, setProductResults] = useState([]);
  const [productLoading, setProductLoading] = useState(false);
  const prodDebounceRef = useRef(null);

  // cart: { [productID]: { product, qty, priceOverride } }
  const [cart, setCart]     = useState({});
  const [overrides, setOverrides] = useState({}); // customer-level price overrides from DB

  // Inline price editing
  const [editingPriceId, setEditingPriceId] = useState(null);
  const [editingPriceVal, setEditingPriceVal] = useState('');

  // Payment type selector (for 'both' customers)
  const [chosenPaymentType, setChosenPaymentType] = useState('invoice');

  // Admin notes
  const [adminNotes, setAdminNotes] = useState('');

  // Submission
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // ── Customer overrides (fetched after selecting customer) ─────────────────────
  const loadCustomerOverrides = useCallback(async (customerId) => {
    try {
      const res = await adminCustomers.getOverrides(customerId);
      const rows = res?.data?.data || [];
      const map = {};
      for (const r of rows) {
        map[r.productID] = parseFloat(r.customPrice);
      }
      setOverrides(map);
    } catch {
      setOverrides({});
    }
  }, []);

  const selectCustomer = (c) => {
    setSelectedCustomer(c);
    setStep(2);
    setCart({});
    setSubmitError('');
    setChosenPaymentType(c.paymentType === 'both' ? 'invoice' : c.paymentType);
    loadCustomerOverrides(c.customerID);
    // Kick off initial product load
    searchProducts('');
  };

  // ── Product search ─────────────────────────────────────────────────────────────
  const searchProducts = useCallback(async (q) => {
    setProductLoading(true);
    try {
      const res = await adminProducts.list({ wholesaleOnly: 'true', search: q || undefined });
      setProductResults(res?.data?.data || []);
    } catch {
      setProductResults([]);
    } finally {
      setProductLoading(false);
    }
  }, []);

  const handleProductQueryChange = (e) => {
    const q = e.target.value;
    setProductQuery(q);
    clearTimeout(prodDebounceRef.current);
    prodDebounceRef.current = setTimeout(() => searchProducts(q), 300);
  };

  // ── Cart helpers ───────────────────────────────────────────────────────────────
  const addToCart = (product) => {
    setCart((prev) => {
      if (prev[product.productID]) {
        return {
          ...prev,
          [product.productID]: {
            ...prev[product.productID],
            qty: prev[product.productID].qty + 1,
          },
        };
      }
      return {
        ...prev,
        [product.productID]: { product, qty: 1, priceOverride: null },
      };
    });
  };

  const updateQty = (productID, delta) => {
    setCart((prev) => {
      const entry = prev[productID];
      if (!entry) return prev;
      const newQty = entry.qty + delta;
      if (newQty <= 0) {
        const next = { ...prev };
        delete next[productID];
        return next;
      }
      return { ...prev, [productID]: { ...entry, qty: newQty } };
    });
  };

  const removeFromCart = (productID) => {
    setCart((prev) => {
      const next = { ...prev };
      delete next[productID];
      return next;
    });
  };

  const startEditPrice = (productID, currentVal) => {
    setEditingPriceId(productID);
    setEditingPriceVal(currentVal !== null && currentVal !== undefined ? String(currentVal) : '');
  };

  const commitEditPrice = (productID) => {
    const val = editingPriceVal.trim();
    const parsed = val === '' ? null : parseFloat(val);
    setCart((prev) => ({
      ...prev,
      [productID]: {
        ...prev[productID],
        priceOverride: (!isNaN(parsed) && parsed >= 0) ? parsed : null,
      },
    }));
    setEditingPriceId(null);
  };

  // ── Resolved price for display ─────────────────────────────────────────────────
  const resolveDisplayPrice = (entry) => {
    const { product, qty, priceOverride } = entry;
    if (priceOverride !== null && priceOverride !== undefined) return priceOverride;
    if (overrides[product.productID] !== undefined)            return overrides[product.productID];
    const level    = selectedCustomer?.priceLevel       || 'standard';
    const discount = parseFloat(selectedCustomer?.priceLevelDiscount || 0);
    if (['silver', 'gold', 'platinum'].includes(level) && discount > 0) {
      return parseFloat(product.wholesalePrice) * (1 - discount / 100);
    }
    return parseFloat(product.wholesalePrice) || 0;
  };

  const cartEntries = Object.values(cart);
  const cartSubtotal = cartEntries.reduce((sum, entry) => {
    return sum + entry.qty * resolveDisplayPrice(entry);
  }, 0);

  // Credit cap check for display warning
  const creditCap  = parseFloat(selectedCustomer?.creditCap || 0);
  const outstanding = parseFloat(selectedCustomer?.outstandingBalance || 0);
  const availableCredit = creditCap > 0 ? creditCap - outstanding : null;
  const exceedsCredit   = availableCredit !== null
    && chosenPaymentType === 'invoice'
    && cartSubtotal > availableCredit;

  // ── Place order ────────────────────────────────────────────────────────────────
  const handlePlaceOrder = async () => {
    if (!cartEntries.length) return;
    setSubmitting(true);
    setSubmitError('');

    const items = cartEntries.map((entry) => ({
      productID:     entry.product.productID,
      qty:           entry.qty,
      priceOverride: entry.priceOverride,
    }));

    try {
      const res = await adminOrders.create({
        customerID:  selectedCustomer.customerID,
        items,
        paymentType: chosenPaymentType,
        adminNotes:  adminNotes || undefined,
      });
      const orderID = res?.data?.data?.orderID;
      if (orderID) {
        navigate(`/admin/orders/${orderID}`);
      }
    } catch (err) {
      setSubmitError(
        err?.response?.data?.message || err?.message || 'Failed to create order.'
      );
      setSubmitting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="container-fluid py-4">
      {/* ── Header ── */}
      <div className="d-flex align-items-center gap-3 mb-4">
        <button
          className="btn btn-link text-decoration-none text-muted p-0"
          onClick={() => step === 2 && !preselectedId ? setStep(1) : navigate('/admin/orders')}
        >
          <ChevronLeft size={20} />
        </button>
        <div>
          <h2 className="mb-0 fw-bold">Create Order</h2>
          <p className="text-muted small mb-0">
            Step {step} of 2 — {step === 1 ? 'Select Customer' : 'Build Order'}
          </p>
        </div>
      </div>

      {/* ── Progress ── */}
      <div className="d-flex align-items-center gap-2 mb-4">
        {[1, 2].map((s) => (
          <React.Fragment key={s}>
            <div
              className={`rounded-circle d-flex align-items-center justify-content-center fw-bold`}
              style={{
                width: 32,
                height: 32,
                background: step >= s ? '#0d6efd' : '#dee2e6',
                color: step >= s ? '#fff' : '#6c757d',
                fontSize: 14,
              }}
            >
              {s}
            </div>
            {s < 2 && (
              <div
                style={{
                  flex: 1,
                  height: 2,
                  background: step > s ? '#0d6efd' : '#dee2e6',
                  maxWidth: 60,
                }}
              />
            )}
          </React.Fragment>
        ))}
        <span className="text-muted small ms-2">
          {step === 1 ? 'Select customer' : 'Build order'}
        </span>
      </div>

      {/* ════════════════════════════════════════════════════════
          STEP 1 — Customer Search
      ════════════════════════════════════════════════════════ */}
      {step === 1 && (
        <div className="row justify-content-center">
          <div className="col-lg-7">
            <div className="card shadow-sm">
              <div className="card-header fw-semibold">
                <Building2 size={16} className="me-2" />
                Find Customer
              </div>
              <div className="card-body">
                <div className="input-group mb-3">
                  <span className="input-group-text">
                    <Search size={15} />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by company name, email, or contact…"
                    value={customerQuery}
                    onChange={handleCustomerQueryChange}
                    autoFocus
                  />
                  {customerQuery && (
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => { setCustomerQuery(''); setCustomerResults([]); }}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>

                {customerLoading && (
                  <div className="text-center py-3 text-muted small">
                    <div className="spinner-border spinner-border-sm me-2" />
                    Searching…
                  </div>
                )}

                {!customerLoading && customerQuery.length >= 2 && customerResults.length === 0 && (
                  <div className="text-center py-4 text-muted small">
                    No customers found.{' '}
                    <Link to="/admin/customers/new" target="_blank" rel="noopener noreferrer">
                      Create new customer
                    </Link>
                  </div>
                )}

                {customerResults.length > 0 && (
                  <div className="list-group">
                    {customerResults.map((c) => (
                      <button
                        key={c.customerID}
                        className="list-group-item list-group-item-action"
                        onClick={() => selectCustomer(c)}
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <div className="fw-semibold">{c.companyName}</div>
                            <div className="text-muted small">
                              <User size={12} className="me-1" />
                              {[c.firstName, c.lastName].filter(Boolean).join(' ') || '—'}
                              {' · '}
                              {c.email}
                            </div>
                          </div>
                          <div className="text-end">
                            <span className="badge bg-light text-dark border me-1">
                              {PAYMENT_LABEL[c.paymentType] || c.paymentType}
                            </span>
                            {!c.isApproved && (
                              <span className="badge bg-warning text-dark">Pending</span>
                            )}
                            {!c.isActive && (
                              <span className="badge bg-danger">Inactive</span>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {!customerQuery && (
                  <div className="text-muted small text-center py-3">
                    Type at least 2 characters to search customers.
                  </div>
                )}

                <div className="mt-3 pt-3 border-top text-end">
                  <Link
                    to="/admin/customers/new"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary btn-sm"
                  >
                    <Plus size={14} className="me-1" />
                    New Customer
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════
          STEP 2 — Build Order
      ════════════════════════════════════════════════════════ */}
      {step === 2 && selectedCustomer && (
        <>
          {/* ── Customer summary bar ── */}
          <div className="card border-primary mb-4">
            <div className="card-body py-2 d-flex flex-wrap align-items-center gap-3">
              <Building2 size={18} className="text-primary" />
              <div>
                <span className="fw-semibold">{selectedCustomer.companyName}</span>
                <span className="text-muted small ms-2">{selectedCustomer.email}</span>
              </div>
              <span className="badge bg-light text-dark border">
                {PAYMENT_LABEL[selectedCustomer.paymentType] || selectedCustomer.paymentType}
              </span>
              {availableCredit !== null && (
                <span className={`small ${availableCredit < 0 ? 'text-danger' : 'text-muted'}`}>
                  Available credit: <strong>{fmt(availableCredit)}</strong>
                </span>
              )}
              {!preselectedId && (
                <button
                  className="btn btn-link btn-sm text-muted p-0 ms-auto"
                  onClick={() => { setStep(1); setSelectedCustomer(null); setCart({}); }}
                >
                  Change customer
                </button>
              )}
            </div>
          </div>

          <div className="row g-4">
            {/* ── Left: Product catalog ── */}
            <div className="col-lg-7">
              <div className="card shadow-sm">
                <div className="card-header fw-semibold d-flex align-items-center justify-content-between">
                  <span>Products</span>
                  {productLoading && <div className="spinner-border spinner-border-sm text-muted" />}
                </div>
                <div className="card-body">
                  <div className="input-group input-group-sm mb-3">
                    <span className="input-group-text">
                      <Search size={13} />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search products…"
                      value={productQuery}
                      onChange={handleProductQueryChange}
                    />
                    {productQuery && (
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => { setProductQuery(''); searchProducts(''); }}
                      >
                        <X size={12} />
                      </button>
                    )}
                  </div>

                  {/* Product list */}
                  <div style={{ maxHeight: 480, overflowY: 'auto' }}>
                    {productResults.length === 0 && !productLoading && (
                      <div className="text-center text-muted small py-4">No products found.</div>
                    )}
                    {productResults.map((p) => {
                      const inCart     = cart[p.productID];
                      const hasOverride = overrides[p.productID] !== undefined;
                      const level      = selectedCustomer?.priceLevel || 'standard';
                      const discount   = parseFloat(selectedCustomer?.priceLevelDiscount || 0);
                      let displayPrice = parseFloat(p.wholesalePrice) || 0;
                      let priceLabel   = 'Base';
                      if (hasOverride) {
                        displayPrice = overrides[p.productID];
                        priceLabel   = 'Override';
                      } else if (['silver', 'gold', 'platinum'].includes(level) && discount > 0) {
                        displayPrice = displayPrice * (1 - discount / 100);
                        priceLabel   = `${level.charAt(0).toUpperCase() + level.slice(1)} (${discount}% off)`;
                      }

                      const { primary: pPrimary, secondary: pSecondary } = formatProductDisplay(p);
                      return (
                        <div
                          key={p.productID}
                          className={`d-flex align-items-center justify-content-between py-2 border-bottom ${inCart ? 'bg-light' : ''}`}
                        >
                          <div className="flex-grow-1 me-3" style={{ minWidth: 0 }}>
                            <div className="fw-semibold text-truncate" style={{ fontSize: 14 }}>
                              {pPrimary}
                            </div>
                            <div className="text-muted" style={{ fontSize: 12 }}>
                              {pSecondary}{p.proPack && `${pSecondary ? ' · ' : ''}${p.proPack}`}
                            </div>
                            <div className="mt-1">
                              <span className="fw-semibold text-primary" style={{ fontSize: 13 }}>
                                {fmt(displayPrice)}
                              </span>
                              <span className="text-muted ms-1" style={{ fontSize: 11 }}>
                                / case · {priceLabel}
                              </span>
                              {hasOverride && (
                                <span className="badge bg-info text-dark ms-1" style={{ fontSize: 10 }}>
                                  custom
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex-shrink-0">
                            {inCart ? (
                              <div className="d-flex align-items-center gap-1">
                                <button
                                  className="btn btn-outline-secondary btn-sm py-0 px-2"
                                  onClick={() => updateQty(p.productID, -1)}
                                >
                                  <Minus size={12} />
                                </button>
                                <span className="px-2 fw-semibold" style={{ minWidth: 28, textAlign: 'center' }}>
                                  {inCart.qty}
                                </span>
                                <button
                                  className="btn btn-outline-secondary btn-sm py-0 px-2"
                                  onClick={() => updateQty(p.productID, 1)}
                                >
                                  <Plus size={12} />
                                </button>
                              </div>
                            ) : (
                              <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => addToCart(p)}
                              >
                                <Plus size={13} className="me-1" />
                                Add
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Right: Order summary ── */}
            <div className="col-lg-5">
              <div className="card shadow-sm">
                <div className="card-header fw-semibold d-flex align-items-center gap-2">
                  <ShoppingCart size={16} />
                  Order Summary
                  {cartEntries.length > 0 && (
                    <span className="badge bg-primary ms-auto">{cartEntries.length}</span>
                  )}
                </div>
                <div className="card-body p-0">
                  {cartEntries.length === 0 ? (
                    <div className="text-center text-muted small py-5">
                      Add products from the catalog.
                    </div>
                  ) : (
                    <>
                      <div style={{ maxHeight: 340, overflowY: 'auto' }}>
                        <table className="table table-sm mb-0">
                          <thead className="table-light sticky-top">
                            <tr>
                              <th style={{ fontSize: 12 }}>Product</th>
                              <th style={{ fontSize: 12 }} className="text-center">Qty</th>
                              <th style={{ fontSize: 12 }} className="text-end">Price</th>
                              <th style={{ fontSize: 12 }} className="text-end">Total</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {cartEntries.map((entry) => {
                              const pid          = entry.product.productID;
                              const resolvedPrice = resolveDisplayPrice(entry);
                              const isEditing    = editingPriceId === pid;
                              return (
                                <tr key={pid}>
                                  <td style={{ fontSize: 12 }}>
                                    <div className="fw-semibold">{entry.product.proFanciful}</div>
                                    <div className="text-muted" style={{ fontSize: 11 }}>
                                      {[entry.product.proVintage, entry.product.proVarietal]
                                        .filter(Boolean)
                                        .join(' · ')}
                                    </div>
                                    {entry.priceOverride !== null && entry.priceOverride !== undefined && (
                                      <span className="badge bg-warning text-dark" style={{ fontSize: 10 }}>
                                        admin price
                                      </span>
                                    )}
                                  </td>
                                  <td className="text-center align-middle" style={{ fontSize: 12 }}>
                                    <div className="d-flex align-items-center justify-content-center gap-1">
                                      <button
                                        className="btn btn-outline-secondary py-0 px-1"
                                        style={{ fontSize: 11 }}
                                        onClick={() => updateQty(pid, -1)}
                                      >
                                        <Minus size={10} />
                                      </button>
                                      <span style={{ minWidth: 22, textAlign: 'center' }}>{entry.qty}</span>
                                      <button
                                        className="btn btn-outline-secondary py-0 px-1"
                                        style={{ fontSize: 11 }}
                                        onClick={() => updateQty(pid, 1)}
                                      >
                                        <Plus size={10} />
                                      </button>
                                    </div>
                                  </td>
                                  <td className="text-end align-middle" style={{ fontSize: 12 }}>
                                    {isEditing ? (
                                      <div className="d-flex align-items-center gap-1 justify-content-end">
                                        <input
                                          type="number"
                                          className="form-control form-control-sm"
                                          style={{ width: 70, fontSize: 11 }}
                                          value={editingPriceVal}
                                          min="0"
                                          step="0.01"
                                          onChange={(e) => setEditingPriceVal(e.target.value)}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') commitEditPrice(pid);
                                            if (e.key === 'Escape') setEditingPriceId(null);
                                          }}
                                          autoFocus
                                        />
                                        <button
                                          className="btn btn-success btn-sm py-0 px-1"
                                          onClick={() => commitEditPrice(pid)}
                                        >
                                          <Check size={11} />
                                        </button>
                                        <button
                                          className="btn btn-outline-secondary btn-sm py-0 px-1"
                                          onClick={() => setEditingPriceId(null)}
                                        >
                                          <X size={11} />
                                        </button>
                                      </div>
                                    ) : (
                                      <span
                                        className="d-inline-flex align-items-center gap-1"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => startEditPrice(pid, entry.priceOverride ?? resolvedPrice)}
                                        title="Click to override price for this order"
                                      >
                                        {fmt(resolvedPrice)}
                                        <Pencil size={10} className="text-muted" />
                                      </span>
                                    )}
                                  </td>
                                  <td className="text-end align-middle" style={{ fontSize: 12 }}>
                                    {fmt(entry.qty * resolvedPrice)}
                                  </td>
                                  <td className="align-middle ps-0">
                                    <button
                                      className="btn btn-link btn-sm text-danger p-0"
                                      onClick={() => removeFromCart(pid)}
                                      title="Remove"
                                    >
                                      <X size={13} />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Subtotal */}
                      <div className="px-3 py-2 border-top">
                        <div className="d-flex justify-content-between fw-bold">
                          <span>Subtotal</span>
                          <span>{fmt(cartSubtotal)}</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Payment type (only for 'both' customers) */}
              {selectedCustomer.paymentType === 'both' && (
                <div className="card shadow-sm mt-3">
                  <div className="card-body">
                    <label className="form-label fw-semibold small mb-2">Payment Method</label>
                    <div className="d-flex gap-3">
                      {['invoice', 'credit_card'].map((type) => (
                        <div className="form-check" key={type}>
                          <input
                            className="form-check-input"
                            type="radio"
                            name="paymentType"
                            id={`pt-${type}`}
                            value={type}
                            checked={chosenPaymentType === type}
                            onChange={() => setChosenPaymentType(type)}
                          />
                          <label className="form-check-label small" htmlFor={`pt-${type}`}>
                            {type === 'invoice' ? 'Invoice' : 'Credit Card'}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Credit warning */}
              {exceedsCredit && (
                <div className="alert alert-warning d-flex align-items-center gap-2 mt-3 py-2">
                  <AlertCircle size={16} />
                  <span className="small">
                    Order exceeds available credit ({fmt(availableCredit)}).
                  </span>
                </div>
              )}

              {/* Admin notes */}
              <div className="card shadow-sm mt-3">
                <div className="card-body">
                  <label className="form-label fw-semibold small mb-1">
                    Admin Notes <span className="text-muted fw-normal">(optional)</span>
                  </label>
                  <textarea
                    className="form-control form-control-sm"
                    rows={2}
                    maxLength={500}
                    placeholder="Internal notes, special instructions…"
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                  />
                </div>
              </div>

              {/* Error */}
              {submitError && (
                <div className="alert alert-danger d-flex align-items-center gap-2 mt-3 py-2">
                  <AlertCircle size={16} />
                  <span className="small">{submitError}</span>
                </div>
              )}

              {/* Place order */}
              <div className="mt-3 d-grid">
                <button
                  className="btn btn-primary"
                  onClick={handlePlaceOrder}
                  disabled={cartEntries.length === 0 || submitting}
                >
                  {submitting
                    ? <><span className="spinner-border spinner-border-sm me-2" />Placing Order…</>
                    : `Place Order — ${fmt(cartSubtotal)}`
                  }
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
